(function () {
  const GRAPHQL_ENDPOINT = 'https://backend.cleansweeps.app/graphql';
  const PLATFORM = 'Shopify';
  // Safety-net poll only — the real triggers are cart events + drawer open
  // (see the trigger model at the bottom of this file). Was a flat 1500ms
  // poll of /cart.js on every page.
  const SAFETY_POLL_MS = 30000;
  const ERROR_BACKOFF_CAP_MS = 300000; // consecutive-failure retry ceiling (5 min)
  const ORG_CACHE_TTL_MS = 60000;

  const TOTAL_ENTRIES_SELECTOR = '#cart-entries-total';
  const ACTIVE_SWEEPSTAKES_SELECTOR = '#cart-active-sweepstakes';
  const ENTRIES_BLOCK_SELECTOR = '#cart-entry-total-block';

  // Pages where this script is wasted work entirely (no product interaction
  // surface; the cart drawer markup is only present via the global header).
  // These skip ALL triggers — behavior parity with the old full skip.
  //   - Games (play-*) and Watch & Win.
  // The cart drawer remains functional on these pages via its own cart.js
  // flows; this widget's entries block simply stays hidden there.
  const POLL_SKIP_PATHS = [
    '/pages/play-snake',
    '/pages/play-2048',
    '/pages/play-froggy',
    '/pages/play-fluffles-bubbles',
    '/pages/play-3d-mahjong',
    '/pages/play-classic-sudoku',
    '/pages/play-solitaire',
    '/pages/play-fluffles-match',
    '/pages/play-simple-word',
    '/pages/play-blockup',
    '/pages/play-blastify',
    '/pages/play-basketball-tap',
    '/pages/play-jigsaw-tiles',
    '/pages/play-word-haven',
    '/pages/games',
    '/pages/watch-and-win'
  ];

  function shouldSkipPolling() {
    const path = (window.location.pathname || '').replace(/\/+$/, '');
    return POLL_SKIP_PATHS.indexOf(path) !== -1;
  }

  let previousCartSignature = null;
  let isUpdating = false;
  // Trailing rerun: a cart event that lands while an update is in flight
  // would otherwise be swallowed by the isUpdating guard, leaving the total
  // computed from a pre-change /cart.js snapshot until the next trigger.
  let pendingRerun = false;
  // Consecutive-failure backoff for the safety poll — event-driven triggers
  // deliberately bypass this (a real cart change should retry immediately).
  let consecutiveErrors = 0;
  let lastErrorAt = 0;
  let organizationCache = null;
  let organizationCacheExpiresAt = 0;
  let previousReferralCode = null;
  let cachedReferralBonus = 0;
  // Per-sweepstake details from the last successful render, keyed by
  // sanitized id. Read by getBonusTiers() for calculateWithDiscount.
  let latestDetailsById = new Map();

  function sanitizeId(value) {
    return String(value == null ? '' : value).replace(/\D/g, '');
  }

  function toNumber(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function safeJsonParse(value, fallback) {
    if (typeof value !== 'string' || value.trim() === '') {
      return fallback;
    }

    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  }

  function getPlatformId() {
    if (typeof window === 'undefined') return null;

    if (typeof window.CS_SHOP_GID === 'string' && window.CS_SHOP_GID.trim()) {
      return window.CS_SHOP_GID;
    }

    if (window.CS_SHOP_ID != null) {
      const shopId = sanitizeId(window.CS_SHOP_ID);
      if (shopId) return `gid://shopify/Shop/${shopId}`;
    }

    return null;
  }

  function getCustomerTagSet() {
    const raw = window.CUSTOMER_TAGS;

    if (!Array.isArray(raw)) {
      return new Set();
    }

    return new Set(
      raw
        .map((tag) => String(tag || '').trim().toLowerCase())
        .filter(Boolean)
    );
  }

  function parseBonusSpending(entryManagementRaw) {
    const entryManagement = safeJsonParse(entryManagementRaw, {});
    const bonusSpending = Array.isArray(entryManagement?.bonusSpending)
      ? entryManagement.bonusSpending
      : [];

    return bonusSpending
      .filter((item) => item?.spendingLimitEnabled === true || item?.spendingLimitEnabled === 'true')
      .map((item) => ({
        spendingLimitDollar: toNumber(item?.spendingLimitDollar, 0),
        bonusEntries: toNumber(item?.bonusEntries, 0),
      }))
      .sort((a, b) => a.spendingLimitDollar - b.spendingLimitDollar);
  }

  function parseOrgDiscountCodes(input) {
    let jsonStr;

    if (typeof input === 'string') {
      jsonStr = input.trim();
    } else if (input && typeof input === 'object') {
      const maybe =
        input?.data?.organization?.discountCodes ??
        input?.organization?.discountCodes ??
        input?.discountCodes;

      if (typeof maybe === 'string') {
        jsonStr = maybe.trim();
      }
    }

    if (!jsonStr) return {};

    let parsed;
    try {
      parsed = JSON.parse(jsonStr);
    } catch (_error) {
      return {};
    }

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const out = {};

    Object.entries(parsed).forEach(([code, cfg]) => {
      if (!code || typeof cfg !== 'object' || cfg === null) return;

      const entryGive = Number(cfg.entryGive);
      const multiplierGive = Number(cfg.multiplierGive);

      out[String(code).toUpperCase()] = {
        entryGive: Number.isFinite(entryGive) ? entryGive : 0,
        multiplierGive: Number.isFinite(multiplierGive) ? multiplierGive : 1,
      };
    });

    return out;
  }

  function getAppliedCartDiscountCodes(discountCodes) {
    if (!Array.isArray(discountCodes)) return [];

    const seen = new Set();
    const out = [];

    discountCodes.forEach((discountCode) => {
      let rawCode = '';
      if (typeof discountCode === 'string') {
        rawCode = discountCode;
      } else if (discountCode && typeof discountCode === 'object') {
        rawCode = String(discountCode.code || '').trim();
      }

      const normalizedCode = rawCode.trim().toUpperCase();
      if (!normalizedCode || seen.has(normalizedCode)) return;

      seen.add(normalizedCode);
      out.push(normalizedCode);
    });

    return out;
  }

  function hasRenderTargets() {
    return Boolean(
      document.querySelector(TOTAL_ENTRIES_SELECTOR) ||
        document.querySelector(ACTIVE_SWEEPSTAKES_SELECTOR) ||
        document.querySelector('#total-entries-value .entries-number')
    );
  }

  function setText(selector, value) {
    const element = document.querySelector(selector);
    if (element) {
      element.textContent = String(value);
    }
  }

  function setEntriesBlockVisible(visible) {
    const entriesBlock = document.querySelector(ENTRIES_BLOCK_SELECTOR);
    if (!entriesBlock) return;
    entriesBlock.hidden = !visible;
  }

  function formatEntriesTotalValue(total) {
    const valueElement = document.querySelector(TOTAL_ENTRIES_SELECTOR);
    const showTicket = valueElement ? valueElement.dataset.showTicket === 'true' : true;
    const formattedTotal = Number(total).toLocaleString('en-US');

    return showTicket ? `🎟 ${formattedTotal}` : formattedTotal;
  }

  function checkForReferral(note) {
    if (typeof note !== 'string' || !note.includes('Referral Code:')) {
      return null;
    }

    const code = note.replace('Referral Code:', '').trim();
    return code || null;
  }

  async function graphqlRequest(query, variables) {
    const response = await fetch(GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error(`GraphQL request failed with status ${response.status}`);
    }

    const payload = await response.json();
    if (payload?.errors?.length) {
      throw new Error(payload.errors[0]?.message || 'GraphQL returned errors');
    }

    return payload?.data;
  }

  async function fetchOrganizationData() {
    const now = Date.now();
    if (organizationCache && now < organizationCacheExpiresAt) {
      return organizationCache;
    }

    const platformId = getPlatformId();
    if (!platformId) {
      return { activeSweepstakes: [], bonusSpendingById: new Map(), orgDiscountCodes: {} };
    }

    const query = `
      query QueryOrganization($organizationId: Int, $platform: String, $platformId: String) {
        organization(id: $organizationId, platform: $platform, platformId: $platformId) {
          discountCodes
          sweepstakes {
            id
            entryManagement
            isActive
          }
        }
      }
    `;

    try {
      const data = await graphqlRequest(query, {
        organizationId: null,
        platform: PLATFORM,
        platformId,
      });

      const sweepstakes = Array.isArray(data?.organization?.sweepstakes)
        ? data.organization.sweepstakes
        : [];

      const activeSweepstakes = sweepstakes.filter((sweepstake) => sweepstake?.isActive === true);
      const bonusSpendingById = new Map();
      const orgDiscountCodes = parseOrgDiscountCodes(data);

      activeSweepstakes.forEach((sweepstake) => {
        const id = sanitizeId(sweepstake?.id);
        if (!id) return;
        bonusSpendingById.set(id, parseBonusSpending(sweepstake?.entryManagement));
      });

      organizationCache = { activeSweepstakes, bonusSpendingById, orgDiscountCodes };
      organizationCacheExpiresAt = now + ORG_CACHE_TTL_MS;

      return organizationCache;
    } catch (error) {
      console.error('Error fetching organization sweepstakes:', error);
      return { activeSweepstakes: [], bonusSpendingById: new Map(), orgDiscountCodes: {} };
    }
  }

  async function fetchSweepstakeDetailsByProductIds(productIds) {
    if (!Array.isArray(productIds) || productIds.length === 0) {
      return [];
    }

    const platformId = getPlatformId();
    if (!platformId) {
      return [];
    }

    const query = `
      query QuerySweepstakeDetailsByProductId($platform: String!, $platformId: String!, $productIds: [String!]) {
        sweepstakeDetailsByProductId(platform: $platform, platformId: $platformId, productIds: $productIds) {
          id
          entryManagement
          productsAllowedByDefault
          bonusEntriesDefault
          scheduledBonusValue
          scheduledBonusActive
          membershipManagement
          calculateWithDiscount
          products(productIds: $productIds) {
            productId
            productAllowed
            productBonusEntries
            productMultiplier
            orderMultiplierAllowed
            orderMultiplier
          }
        }
      }
    `;

    try {
      const data = await graphqlRequest(query, {
        platform: PLATFORM,
        platformId,
        productIds,
      });

      return Array.isArray(data?.sweepstakeDetailsByProductId) ? data.sweepstakeDetailsByProductId : [];
    } catch (error) {
      console.error('Error fetching sweepstake details by product:', error);
      return [];
    }
  }

  async function validateReferralCode(referralCode) {
    const platformId = getPlatformId();
    if (!platformId || !referralCode) {
      return 0;
    }

    const query = `
      query ValidateReferralCode($referralCode: String, $platform: String, $platformId: String) {
        validateReferralCode(referralCode: $referralCode, platform: $platform, platformId: $platformId) {
          isDeleted
          referralBonusGive
        }
      }
    `;

    try {
      const data = await graphqlRequest(query, {
        referralCode,
        platform: PLATFORM,
        platformId,
      });

      const result = data?.validateReferralCode;
      if (!result || result.isDeleted) {
        return 0;
      }

      return toNumber(result.referralBonusGive, 0);
    } catch (error) {
      console.error('Error validating referral code:', error);
      return 0;
    }
  }

  function buildProductMap(products) {
    const productMap = new Map();

    if (!Array.isArray(products)) {
      return productMap;
    }

    products.forEach((product) => {
      const productId = product?.productId;
      if (!productId) return;

      productMap.set(productId, {
        productAllowed: product?.productAllowed !== false,
        productBonusEntries: toNumber(product?.productBonusEntries, 0),
        productMultiplier: toNumber(product?.productMultiplier, 1),
        orderMultiplierAllowed: product?.orderMultiplierAllowed === true,
        orderMultiplier: toNumber(product?.orderMultiplier, 1),
      });
    });

    return productMap;
  }

  function calculateSweepstakeTotalEntries(sweepstakeDetail, bonusSpendingRules, cart, customerTagSet) {
    const entryManagement = safeJsonParse(sweepstakeDetail?.entryManagement, {});
    const dollarSpent = toNumber(entryManagement?.dollarSpent, 1);
    const baseEntriesPer = toNumber(entryManagement?.entryPer, 0);
    const roundUp = entryManagement?.roundUp === true;
    const roundingFunction = roundUp ? Math.ceil : Math.floor;

    const scheduledBonusActive = sweepstakeDetail?.scheduledBonusActive === true;
    const scheduledBonusValue = toNumber(sweepstakeDetail?.scheduledBonusValue, 0);
    const entriesPer = scheduledBonusActive && scheduledBonusValue > 0 ? scheduledBonusValue : baseEntriesPer;

    const productsAllowedByDefault = sweepstakeDetail?.productsAllowedByDefault === true;
    const bonusEntriesDefault = toNumber(sweepstakeDetail?.bonusEntriesDefault, 0);
    const productMap = buildProductMap(sweepstakeDetail?.products);

    let totalProductEntries = 0;
    let orderMultiplier = 1;

    cart.items.forEach((item) => {
      const quantity = toNumber(item?.quantity, 1);
      const productId = `gid://shopify/Product/${item.product_id}`;
      const override = productMap.get(productId);

      let productAllowed = productsAllowedByDefault;
      let productBonusEntries = bonusEntriesDefault;
      let productMultiplier = 1;

      if (override) {
        productAllowed = override.productAllowed;
        productBonusEntries = override.productBonusEntries;
        productMultiplier = override.productMultiplier;

        if (override.orderMultiplierAllowed && override.orderMultiplier > orderMultiplier) {
          orderMultiplier = override.orderMultiplier;
        }
      }

      if (!productAllowed) {
        return;
      }

      const lineSubtotalCents = sweepstakeDetail?.calculateWithDiscount === true
        ? toNumber(item?.line_price, 0)
        : toNumber(item?.original_line_price, 0);

      const lineSubtotal = lineSubtotalCents / 100;
      const baseEntries = dollarSpent > 0
        ? roundingFunction((entriesPer / dollarSpent) * roundingFunction(lineSubtotal)) * productMultiplier
        : 0;

      totalProductEntries += baseEntries + productBonusEntries * quantity;
    });

    const subtotalCents = sweepstakeDetail?.calculateWithDiscount === true
      ? toNumber(cart?.items_subtotal_price, 0)
      : toNumber(cart?.original_total_price, 0);

    const subtotal = subtotalCents / 100;

    let totalBonusEntries = 0;
    bonusSpendingRules.forEach((bonusRule) => {
      if (subtotal >= toNumber(bonusRule?.spendingLimitDollar, 0)) {
        totalBonusEntries += toNumber(bonusRule?.bonusEntries, 0);
      }
    });

    let membershipBonusEntries = 0;
    let membershipMultiplier = 1;

    const membershipRules = safeJsonParse(sweepstakeDetail?.membershipManagement, []);
    if (Array.isArray(membershipRules) && customerTagSet.size > 0) {
      membershipRules.forEach((rule) => {
        const tag = String(rule?.tag || '').trim().toLowerCase();
        if (!tag || !customerTagSet.has(tag)) {
          return;
        }

        membershipBonusEntries += toNumber(rule?.bonusEntries, 0);
        membershipMultiplier = Math.max(membershipMultiplier, toNumber(rule?.multiplier, 1));
      });
    }

    orderMultiplier = Math.max(orderMultiplier, membershipMultiplier, 1);

    const totalBeforeMultiplier = totalProductEntries + totalBonusEntries + membershipBonusEntries;
    const totalAfterMultiplier = totalBeforeMultiplier * orderMultiplier;

    return Math.max(0, Math.round(totalAfterMultiplier));
  }

  function pickBestDiscountConfig(appliedCodes, orgDiscountCodes) {
    let selectedDiscountConfig = null;

    appliedCodes.forEach((code) => {
      const config = orgDiscountCodes[code];
      if (!config) return;

      const candidate = {
        code,
        entryGive: Math.max(0, toNumber(config.entryGive, 0)),
        multiplierGive: Math.max(1, toNumber(config.multiplierGive, 1)),
      };

      if (!selectedDiscountConfig) {
        selectedDiscountConfig = candidate;
        return;
      }

      if (candidate.multiplierGive > selectedDiscountConfig.multiplierGive) {
        selectedDiscountConfig = candidate;
        return;
      }

      if (
        candidate.multiplierGive === selectedDiscountConfig.multiplierGive &&
        candidate.entryGive > selectedDiscountConfig.entryGive
      ) {
        selectedDiscountConfig = candidate;
      }
    });

    return selectedDiscountConfig;
  }

  function getCartSignature(cart) {
    return JSON.stringify({
      item_count: cart?.item_count,
      total_price: cart?.total_price,
      original_total_price: cart?.original_total_price,
      items_subtotal_price: cart?.items_subtotal_price,
      note: cart?.note || '',
      discount_codes: Array.isArray(cart?.discount_codes)
        ? cart.discount_codes.map((discountCode) => {
            if (typeof discountCode === 'string') return discountCode;
            if (discountCode && typeof discountCode === 'object') return String(discountCode.code || '');
            return '';
          })
        : [],
      items: Array.isArray(cart?.items)
        ? cart.items.map((item) => ({
            key: item?.key,
            quantity: item?.quantity,
            line_price: item?.line_price,
            original_line_price: item?.original_line_price,
          }))
        : [],
    });
  }

  async function fetchCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    return response.json();
  }

  async function updateEntriesDisplay() {
    if (!hasRenderTargets()) {
      return;
    }

    if (isUpdating) {
      pendingRerun = true;
      return;
    }

    isUpdating = true;

    try {
      const cart = await fetchCart();
      const cartSignature = getCartSignature(cart);
      const entriesBlock = document.querySelector(ENTRIES_BLOCK_SELECTOR);
      const shouldForceRefresh = Boolean(entriesBlock && entriesBlock.hidden);

      if (cartSignature === previousCartSignature && !shouldForceRefresh) {
        return;
      }

      const customerTagSet = getCustomerTagSet();
      const productIds = Array.from(
        new Set(
          (Array.isArray(cart?.items) ? cart.items : []).map(
            (item) => `gid://shopify/Product/${item.product_id}`
          )
        )
      );

      const [organizationData, sweepstakeDetails] = await Promise.all([
        fetchOrganizationData(),
        fetchSweepstakeDetailsByProductIds(productIds),
      ]);

      const activeSweepstakes = Array.isArray(organizationData?.activeSweepstakes)
        ? organizationData.activeSweepstakes
        : [];

      const orgDiscountCodes = organizationData?.orgDiscountCodes || {};
      const appliedCodes = getAppliedCartDiscountCodes(cart?.discount_codes);
      const bestDiscount = pickBestDiscountConfig(appliedCodes, orgDiscountCodes);
      const discountCodeEntries = bestDiscount?.entryGive || 0;
      const discountCodeMultiplier = bestDiscount?.multiplierGive || 1;

      const detailsById = new Map();
      sweepstakeDetails.forEach((detail) => {
        const id = sanitizeId(detail?.id);
        if (id) {
          detailsById.set(id, detail);
        }
      });
      latestDetailsById = detailsById;

      const referralCode = checkForReferral(cart?.note || '');
      if (referralCode !== previousReferralCode) {
        previousReferralCode = referralCode;
        cachedReferralBonus = referralCode ? await validateReferralCode(referralCode) : 0;
      }

      // "Active Giveaways" here means the giveaways THIS cart actually earns entries in
      // (entries > 0), matching Shopify checkout's count — not every org-wide active
      // sweepstake. One the cart doesn't feed contributes 0 entries and is not counted.
      let totalEntriesAcrossActiveSweepstakes = 0;
      let enteredGiveawaysCount = 0;

      activeSweepstakes.forEach((activeSweepstake) => {
        const sweepstakeId = sanitizeId(activeSweepstake?.id);
        if (!sweepstakeId) {
          return;
        }

        const detail = detailsById.get(sweepstakeId) || {
          id: activeSweepstake?.id,
          entryManagement: activeSweepstake?.entryManagement,
          productsAllowedByDefault: false,
          bonusEntriesDefault: 0,
          scheduledBonusValue: null,
          scheduledBonusActive: false,
          membershipManagement: null,
          calculateWithDiscount: false,
          products: [],
        };

        const bonusSpendingRules = organizationData?.bonusSpendingById?.get(sweepstakeId) || [];

        const sweepstakeEntries = calculateSweepstakeTotalEntries(
          detail,
          bonusSpendingRules,
          cart,
          customerTagSet
        );
        if (sweepstakeEntries > 0) {
          enteredGiveawaysCount += 1;
        }
        totalEntriesAcrossActiveSweepstakes += sweepstakeEntries;
      });

      const finalTotalEntries = Math.max(
        0,
        Math.round(
          (totalEntriesAcrossActiveSweepstakes + cachedReferralBonus + discountCodeEntries) *
            discountCodeMultiplier
        )
      );

      setText(TOTAL_ENTRIES_SELECTOR, formatEntriesTotalValue(finalTotalEntries));
      setText(ACTIVE_SWEEPSTAKES_SELECTOR, enteredGiveawaysCount);
      setEntriesBlockVisible(true);

      const legacyTotalEntries = document.querySelector('#total-entries-value .entries-number');
      if (legacyTotalEntries) {
        legacyTotalEntries.textContent = String(finalTotalEntries);
      }

      window.CS_CART_ENTRIES = {
        entries_total: finalTotalEntries,
        numberofactivesweepstaces: enteredGiveawaysCount,
        discount_code_entries: discountCodeEntries,
        discount_code_multiplier: discountCodeMultiplier,
      };

      document.dispatchEvent(
        new CustomEvent('cs:cart-entries-updated', {
          detail: window.CS_CART_ENTRIES,
        })
      );

      // Only cache signature after a full successful render so transient
      // network/timing failures retry on the next trigger.
      previousCartSignature = cartSignature;
      consecutiveErrors = 0;
    } catch (error) {
      console.error('Error updating cart entries display:', error);
      setText(TOTAL_ENTRIES_SELECTOR, formatEntriesTotalValue(0));
      setText(ACTIVE_SWEEPSTAKES_SELECTOR, 0);
      setEntriesBlockVisible(false);
      // Failure leaves the entries block hidden, which sets
      // shouldForceRefresh on the next run — combined with the old 1.5s
      // interval this was a full-pipeline request loop whenever the vendor
      // was unreachable (outage or ad-blocked client). The safety poll now
      // rate-limits retries via this counter instead.
      consecutiveErrors += 1;
      lastErrorAt = Date.now();
    } finally {
      isUpdating = false;
      if (pendingRerun) {
        pendingRerun = false;
        window.setTimeout(updateEntriesDisplay, 0);
      }
    }
  }

  // ---- Public: spend-bonus tiers for the cart drawer bonus bar ----
  //
  // Resolves a sweepstake the same way price-fetch.js does: exact id match,
  // otherwise the first active sweepstake ("default" sanitizes to '' and so
  // always falls through). Reuses the cached organization query, so the
  // drawer bar adds no extra Clean Sweeps requests.
  //
  // Resolves to { sweepstakeId, tiers: [{ dollars, entries }], calculateWithDiscount }
  // or null when nothing resolves. Tiers are cumulative: every tier the
  // subtotal reaches adds its entries (see calculateSweepstakeTotalEntries).
  async function getBonusTiers(requestedSweepstakeId) {
    const organizationData = await fetchOrganizationData();
    const activeSweepstakes = organizationData?.activeSweepstakes || [];
    const requestedId = sanitizeId(requestedSweepstakeId);

    const matchedSweepstake =
      activeSweepstakes.find((sweepstake) => sanitizeId(sweepstake?.id) === requestedId) ||
      activeSweepstakes[0];
    const sweepstakeId = sanitizeId(matchedSweepstake?.id);
    if (!sweepstakeId) return null;

    const tiers = (organizationData.bonusSpendingById.get(sweepstakeId) || [])
      .filter((rule) => rule.spendingLimitDollar > 0 && rule.bonusEntries > 0)
      .map((rule) => ({ dollars: rule.spendingLimitDollar, entries: rule.bonusEntries }));

    return {
      sweepstakeId,
      tiers,
      calculateWithDiscount: latestDetailsById.get(sweepstakeId)?.calculateWithDiscount === true,
    };
  }

  window.CS_BONUS_TIERS = { getBonusTiers };

  // ---- Trigger model ----
  //
  // History: this vendor-origin script polled /cart.js every 1500ms on every
  // page, forever. The cart-signature guard only short-circuited the vendor
  // GraphQL calls, NOT the /cart.js round-trip (cf-cache-status: DYNAMIC on
  // every tick — ~57K requests/day per idle foreground tab). Worse, any
  // pipeline failure hid the entries block without caching the signature, so
  // the next tick force-refreshed the FULL vendor pipeline — an unreachable
  // backend.cleansweeps.app (outage, or the ad-blocker/security-suite
  // environments sized in the 2026-08 nofill investigation) turned every
  // open tab into a 1.5s request loop.
  //
  // Now event-driven, copied from cart-drawer-bonus-bar.js (same drawer,
  // proven pattern): recompute at the moments a cart can actually change —
  // initial load; 'cart:update' (theme) / 'cart:updated' / 'cart:item-added'
  // (ours); the cart drawer dialog opening; the tab becoming visible again
  // (the cart may have changed in another tab). A visibility-gated
  // SAFETY_POLL_MS poll covers mutations that bypass events (e.g. another
  // app editing the cart note/referral), with exponential backoff after
  // consecutive failures. Event triggers bypass the backoff on purpose.
  function safetyPollTick() {
    if (document.visibilityState !== 'visible') return;
    if (consecutiveErrors > 0) {
      const backoff = Math.min(
        SAFETY_POLL_MS * Math.pow(2, consecutiveErrors - 1),
        ERROR_BACKOFF_CAP_MS
      );
      if (Date.now() - lastErrorAt < backoff) return;
    }
    updateEntriesDisplay();
  }

  function refreshNow() {
    updateEntriesDisplay();
  }

  function initTriggers() {
    // Full skip on pages with no cart interaction surface (games / W&W) —
    // behavior parity with the old skip list.
    if (shouldSkipPolling()) {
      return;
    }

    updateEntriesDisplay();

    document.addEventListener('cart:update', refreshNow);
    document.addEventListener('cart:updated', refreshNow);
    document.addEventListener('cart:item-added', refreshNow);

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') refreshNow();
    });

    // Re-fetch when the cart drawer dialog opens (same observer pattern as
    // cart-drawer-bonus-bar.js).
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer) {
      const dialog = drawer.querySelector('dialog');
      if (dialog) {
        new MutationObserver(function (mutations) {
          for (let i = 0; i < mutations.length; i++) {
            if (mutations[i].attributeName === 'open' && dialog.hasAttribute('open')) {
              refreshNow();
              break;
            }
          }
        }).observe(dialog, { attributes: true });
      }
    }

    window.setInterval(safetyPollTick, SAFETY_POLL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTriggers);
  } else {
    initTriggers();
  }
})();
